/* eslint-disable */
import { useState } from "react";
import { Box, Button, FormControl, MenuItem, Select, styled, TextField, Typography, useTheme, Dialog } from "@mui/material";
import { useMutation } from "@apollo/client";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LinkIcon from "@mui/icons-material/Link";
import { GET_LATEST_PROJECT_ID } from "../../../gql/mutations/scrapy";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { ArticleDetailStyle, ImportMenuWrapper } from "./css";
import { Snackbar, Alert } from "@mui/material";
import { useNavigate } from "react-router-dom";

const CustomMenuItem = styled(MenuItem)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  "&.Mui-selected": { color: theme.palette.primary.main },
  "&:hover": {
    color: theme.palette.primary.main,
  },
  "& .ColorBox": {
    width: "18px",
    height: "18px",
    borderRadius: "50px",
    border: `1px solid ${theme.palette.mode === "dark" ? "#ffffff" : "#2A3547"}`,
  },
}));

function ArticleDetail({ listArrayState, title, onAutoInsertInternalLinks, projectMode, projectHomepage, userId, mode = "keywords", entities = [], loading = false, onKeywordClick, activeKeyword }) {
  const [autoInsert, setAutoInsert] = useState(10);
  const [searchText, setSearchText] = useState("");
  const [copySnackbarOpen, setCopySnackbarOpen] = useState(false);
  const [copyMessage, setCopyMessage] = useState("Copied to clipboard");
  const [openProjectModePopup, setOpenProjectModePopup] = useState(false);
  const navigate = useNavigate();
  const [getLatestProjectId, { loading: projectLoading }] = useMutation(GET_LATEST_PROJECT_ID);
  const [autoInsertLoading, setAutoInsertLoading] = useState(false);

  const handleChange = event => {
    setAutoInsert(event.target.value);
  };
  const [anchorElAutoInsert, setAnchorElAutoInsert] = useState(null);
  const [openConfirmAutoInsert, setOpenConfirmAutoInsert] = useState(false);
  const [pendingKeywords, setPendingKeywords] = useState([]);

  const openAutoInsert = Boolean(anchorElAutoInsert);
  const handleClickAutoInsert = event => {
    setAnchorElAutoInsert(event.currentTarget);
  };
  const handleCloseAutoInsert = () => {
    setAnchorElAutoInsert(null);
  };
  const [anchorElCopy, setAnchorElCopy] = useState(null);
  const openCopy = Boolean(anchorElCopy);
  const handleClickCopy = event => {
    setAnchorElCopy(event.currentTarget);
  };
  const handleCloseCopy = () => {
    setAnchorElCopy(null);
  };

  const getExpectedRange = expectedCount => {
    if (!expectedCount) return [0, 0];
    const [min, max] = expectedCount.split("-").map(Number);
    return [min, max];
  };

  const getKeywordStatus = item => {
    const count = Number(item.originalCount || 0);
    const [min, max] = getExpectedRange(item.expectedCount);

    if (count === 0) return "not-used";
    if (count < min) return "use-more";
    if (count >= min && count <= max) return "in-range";
    if (count <= Math.ceil(max * 1.2)) return "above-range";
    return "use-less";
  };

  const STATUS_STYLE_MAP = {
    "not-used": {
      bg: "#E9EDF5",
      border: "#BFC9DD",
      text: "#2A3547",
    },
    "use-more": {
      bg: "#EAF6EF",
      border: "#9ED3B5",
      text: "#137450",
    },
    "in-range": {
      bg: "#E6F4EA",
      border: "#8ED1A2",
      text: "#1E7F43",
    },
    "above-range": {
      bg: "#FFF4E5",
      border: "#FFD39B",
      text: "#8A5A00",
    },
    "use-less": {
      bg: "#FDECEC",
      border: "#F5B5B5",
      text: "#A94442",
    },
  };

  const STATUS_MAP = {
    10: null, // All terms
    20: "not-used",
    30: "use-more",
    40: "in-range",
    50: "above-range",
    60: "use-less",
  };

  // console.log("listarray", listArrayState);
  // console.log("title->", title);

  const filteredList = listArrayState
    ?.map(list => {
      const filteredItems = list.listItems.filter(item => {
        const query = searchText.toLowerCase();
        const selectedStatus = STATUS_MAP[autoInsert];
        const itemStatus = getKeywordStatus(item);

        const matchesSearch = item.title.toLowerCase().includes(query);

        const matchesStatus = !selectedStatus || itemStatus === selectedStatus;

        return matchesSearch && matchesStatus;
      });

      return {
        ...list,
        listItems: filteredItems.map(item => ({
          ...item,
          status: getKeywordStatus(item), // 🔥 attach computed status
        })),
      };
    })
    .filter(list => list.listItems.length > 0);

  const copyToClipboard = async text => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      console.error("Copy failed", err);
    }
  };
  const getVisibleTermsBySection = () => {
    return filteredList.map(section => ({
      sectionTitle: section.title,
      items: section.listItems,
    }));
  };
  const copyVisibleTerms = () => {
    const sections = getVisibleTermsBySection();

    const text = sections
      .map(section => {
        const keywords = section.items.map(item => `- ${item.title}`).join("\n");
        return `${section.sectionTitle}\n${keywords}`;
      })
      .join("\n\n");

    copyToClipboard(text);
    showCopyPopup("Visible keywords copied");
  };
  const copySuggestedUseTerms = () => {
    const sections = getVisibleTermsBySection();

    const text = sections
      .map(section => {
        const filteredItems = section.items.filter(item => {
          const count = Number(item.originalCount || 0);
          return count >= 1 && count <= 3;
        });

        if (filteredItems.length === 0) return null;

        const keywords = filteredItems.map(item => `- ${item.title} (${item.originalCount}x)`).join("\n");

        return `${section.sectionTitle}\n${keywords}`;
      })
      .filter(Boolean)
      .join("\n\n");

    copyToClipboard(text);
    showCopyPopup("Suggested keywords copied");
  };
  const copyTermsAsJSON = () => {
    const sections = getVisibleTermsBySection();

    const jsonData = sections.map(section => ({
      category: section.sectionTitle,
      terms: section.items.map(item => ({
        title: item.title,
        count: item.originalCount,
        expected: item.expectedCount,
        status: item.status,
      })),
    }));

    copyToClipboard(JSON.stringify(jsonData, null, 2));
    showCopyPopup("JSON copied");
  };

  const copyChatGPTPrompt = () => {
    const sections = filteredList
      .map(section => {
        if (!section.listItems || section.listItems.length === 0) return null;

        return {
          title: section.title,
          terms: section.listItems.map(item => item.title),
        };
      })
      .filter(Boolean);

    if (sections.length === 0) return;

    let prompt = `You are an SEO content assistant.

Use the following keywords naturally in the content.
Do NOT keyword stuff.
Maintain readability and context.

Write a 2000-word blog post on the topic ${title}.
Start with catchy H1 using keywords from the [[H1TERMS]] list, and under H1 add a short summary of the subject and explain why article is worth reading.
Secondly, prepare article outline with around 10 H2 subheaders enriched with keywords from the list, a lot of them should be questions.
Thirdly, for each H2 subheader, add two or three paragraphs with detailed explanation for specific subheader. Paragraphs must include terms from the list.
Close article with bullet point summary of most important things to remember.

`;

    sections.forEach(section => {
      prompt += `${section.title}:\n`;
      section.terms.forEach(term => {
        prompt += `- ${term}\n`;
      });
      prompt += `\n`;
    });

    navigator.clipboard.writeText(prompt);
    showCopyPopup("ChatGPT prompt copied");
  };

  const showCopyPopup = (message = "Copied to clipboard") => {
    setCopyMessage(message);
    setCopySnackbarOpen(true);
  };

  const handleCloseCopyPopup = (_, reason) => {
    if (reason === "clickaway") return;
    setCopySnackbarOpen(false);
  };

  if (mode === "entities") {
    return (
      <ArticleDetailStyle>
        <Box sx={{ pt: 2 }}>
          {loading ? (
            <Typography variant="body2" color="text.secondary" sx={{fontSize: 13}}>
              Analyzing content and extracting entities…
            </Typography>
          ) : entities.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }}>
              No entities found.
            </Typography>
          ) : (
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: 13,fontWeight: 600, display: "block", mb: 0.5 }}>
                The following entities have been identified to assist you in writing high-quality, informative content and to simplify the process of research and sourcing.
              </Typography>
              <br />
              {entities.map((entity, idx) => (
                <Box
                  key={idx}
                  sx={{
                    mb: 1.5,
                    p: 1.5,
                    borderRadius: 2,
                    background: "rgba(255,255,255,0.03)",
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Typography sx={{ fontWeight: 600 }}>{entity.keyword}</Typography>

                  <Typography variant="caption" color="primary" component="a" href={entity.wikiUrl} target="_blank" rel="noopener noreferrer" sx={{ textDecoration: "none" }}>
                    View on Wikipedia →
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </ArticleDetailStyle>
    );
  }

  return (
    <ArticleDetailStyle>
      <Box className="DropDownWrapper">
        <FormControl className="FilledDropDown">
          <Button disableElevation onClick={handleClickAutoInsert}>
            <AutoFixHighIcon /> Auto Insert <KeyboardArrowDownIcon className="ExpandMoreIcon" />
          </Button>
          <ImportMenuWrapper
            slotProps={{
              list: {
                "aria-labelledby": "demo-customized-button",
              },
            }}
            anchorEl={anchorElAutoInsert}
            open={openAutoInsert}
            onClose={handleCloseAutoInsert}
          >
            {/* <MenuItem disableRipple onClick={handleCloseAutoInsert}>
              <LinkIcon /> Auto-insert internal links
            </MenuItem> */}
            {/* <MenuItem
              disableRipple
              onClick={() => {
                onAutoInsertInternalLinks({
                  keywords: filteredList.flatMap(section =>
                    section.listItems.map(item => item.title)
                  ),
                });
                handleCloseAutoInsert();
              }}
            >
              <LinkIcon /> Auto-insert internal links
            </MenuItem> */}
            {/* <MenuItem
              disableRipple
              onClick={() => {
                if (projectMode !== "domain") {
                  handleCloseAutoInsert();
                  setOpenProjectModePopup(true);
                  return;
                }

                onAutoInsertInternalLinks({
                  keywords: filteredList.flatMap(section =>
                    section.listItems.map(item => item.title)
                  ),
                });

                handleCloseAutoInsert();
              }}
            >
              <LinkIcon /> Auto-insert internal links
            </MenuItem> */}
            <MenuItem
              disableRipple
              onClick={() => {
                if (projectMode !== "domain") {
                  handleCloseAutoInsert();
                  setOpenProjectModePopup(true);
                  return;
                }

                const keywords = filteredList.flatMap(section => section.listItems.map(item => item.title));

                setPendingKeywords(keywords);
                setOpenConfirmAutoInsert(true);
                handleCloseAutoInsert();
              }}
            >
              <LinkIcon /> Auto-insert internal links
            </MenuItem>
          </ImportMenuWrapper>
        </FormControl>
        {/* <FormControl className="BasicInput">
          <TextField variant="outlined" placeholder="Type to search." />
        </FormControl> */}
        <FormControl className="BasicInput">
          <TextField variant="outlined" placeholder="Type to search." value={searchText} onChange={e => setSearchText(e.target.value)} />
        </FormControl>
        <FormControl className="BasicDropDown">
          <Select id="demo-simple-select" value={autoInsert} onChange={handleChange} IconComponent={ExpandMoreIcon}>
            <CustomMenuItem value={10}>All terms</CustomMenuItem>
            <CustomMenuItem value={20}>
              <Box
                className="ColorBox"
                sx={{
                  backgroundColor: "#717070",
                }}
              />{" "}
              Not used
            </CustomMenuItem>
            <CustomMenuItem value={30}>
              <Box
                className="ColorBox"
                sx={{
                  backgroundColor: "#137450",
                }}
              />{" "}
              Consider using more
            </CustomMenuItem>
            <CustomMenuItem value={40}>
              <Box
                className="ColorBox"
                sx={{
                  backgroundColor: "#2d5345",
                }}
              />{" "}
              In suggested range
            </CustomMenuItem>
            <CustomMenuItem value={50}>
              <Box
                className="ColorBox"
                sx={{
                  backgroundColor: "#775a08",
                }}
              />{" "}
              Slightly above range
            </CustomMenuItem>
            <CustomMenuItem value={60}>
              <Box
                className="ColorBox"
                sx={{
                  backgroundColor: "#6c4554",
                }}
              />{" "}
              Consider using less
            </CustomMenuItem>
          </Select>
        </FormControl>
        <FormControl className="BasicMenuDropDown">
          <Button disableElevation onClick={handleClickCopy}>
            <ContentCopyRoundedIcon /> Copy... <KeyboardArrowDownIcon className="ExpandMoreIcon" />
          </Button>
          <ImportMenuWrapper
            slotProps={{
              list: {
                "aria-labelledby": "demo-customized-button",
              },
            }}
            anchorEl={anchorElCopy}
            open={openCopy}
            onClose={handleCloseCopy}
          >
            {/* <MenuItem disableRipple onClick={handleCloseCopy}>
              List of visible terms
            </MenuItem> */}
            <MenuItem
              disableRipple
              onClick={() => {
                copyVisibleTerms();
                handleCloseCopy();
              }}
            >
              List of visible terms
            </MenuItem>
            {/* <MenuItem disableRipple onClick={handleCloseCopy}>
              Terms with suggested uses ( term: 1-3x )
            </MenuItem> */}
            <MenuItem
              disableRipple
              onClick={() => {
                copySuggestedUseTerms();
                handleCloseCopy();
              }}
            >
              Terms with suggested uses ( term: 1-3x )
            </MenuItem>

            {/* <MenuItem disableRipple onClick={handleCloseCopy}>
              ChatGPT prompt with suggested terms
            </MenuItem> */}
            <MenuItem
              disableRipple
              onClick={() => {
                copyChatGPTPrompt();
                handleCloseCopy();
              }}
            >
              ChatGPT prompt with suggested terms
            </MenuItem>
            {/* <MenuItem disableRipple onClick={handleCloseCopy}>
              Copy terms in JSON
            </MenuItem> */}
            <MenuItem
              disableRipple
              onClick={() => {
                copyTermsAsJSON();
                handleCloseCopy();
              }}
            >
              Copy terms in JSON
            </MenuItem>
          </ImportMenuWrapper>
        </FormControl>
      </Box>
      <Box className="GeneratedListWrapper">
        {filteredList?.length === 0 ? (
          <Box
            sx={{
              textAlign: "center",
              padding: "24px",
              color: "text.secondary",
            }}
          >
            <Typography variant="body1">No results found</Typography>
          </Box>
        ) : (
          filteredList?.map(list => (
            <Box className="MainBlockWrapper" key={list.id}>
              <Box className="BlockHeadWrapper">
                <InfoOutlinedIcon className="InfoIcon" />
                {list.title}: <ContentCopyRoundedIcon className="CopyIcon" />
                <ExpandMoreIcon className="DownArrow" />
              </Box>

              <Box className="ListBlockWrapper">
                {list.listItems.map(item => (
                  // <Box className={`ListInnerWrapper ${item.status || ""}`} key={item.id}>
                  <Box
                    key={item.id}
                    className="ListInnerWrapper"
                    onClick={(e) => { onKeywordClick && onKeywordClick(item.title, e); }}
                    sx={{
                      backgroundColor: activeKeyword === item.title ? "#BBDEFB" : STATUS_STYLE_MAP[item.status]?.bg,
                      border: activeKeyword === item.title ? "2px solid #1976d2" : `1px solid ${STATUS_STYLE_MAP[item.status]?.border}`,
                      borderRadius: 1,
                      color: STATUS_STYLE_MAP[item.status]?.text,
                      cursor: onKeywordClick ? "pointer" : "default",
                      transition: "all 0.15s ease",
                      "&:hover": onKeywordClick ? { opacity: 0.85 } : {},
                    }}
                  >
                    <Typography component="p">
                      {item.title}
                      <Typography component="span">{item.value}</Typography>
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          ))
        )}
      </Box>
      <Snackbar open={copySnackbarOpen} autoHideDuration={2000} onClose={handleCloseCopyPopup} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert onClose={handleCloseCopyPopup} severity="success" variant="filled" sx={{ width: "100%" }}>
          {copyMessage}
        </Alert>
      </Snackbar>
      <Dialog open={openProjectModePopup} onClose={() => setOpenProjectModePopup(false)} maxWidth="sm" fullWidth>
        <Box sx={{ p: 3, textAlign: "center" }}>
          <Typography variant="h6" fontWeight={600}>
            Action not available
          </Typography>

          <Typography sx={{ mt: 2 }}>
            Internal links suggestions are only available in domain-related projects.
            <br />
            Please switch the project mode to a domain-related project and enter the homepage URL in the project settings.
          </Typography>

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              gap: 2,
              mt: 4,
            }}
          >
            <Button variant="outlined" onClick={() => setOpenProjectModePopup(false)}>
              Cancel
            </Button>

            <Button
              variant="contained"
              onClick={() => {
                setOpenProjectModePopup(false);
                navigate("/content-score");
              }}
            >
              Update settings
            </Button>
          </Box>
        </Box>
      </Dialog>
      <Dialog open={openConfirmAutoInsert} onClose={() => setOpenConfirmAutoInsert(false)} maxWidth="xs" fullWidth>
        <Box sx={{ p: 3, textAlign: "center" }}>
          <Typography variant="h6" fontWeight={600}>
            Confirm action
          </Typography>

          <Typography sx={{ mt: 2 }}>Are you sure you want to auto insert internal links?</Typography>

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              gap: 2,
              mt: 4,
            }}
          >
            <Button variant="outlined" disabled={autoInsertLoading} onClick={() => setOpenConfirmAutoInsert(false)}>
              Cancel
            </Button>

            {/* <Button
              variant="contained"
              onClick={() => {
                onAutoInsertInternalLinks({ keywords: pendingKeywords });
                setOpenConfirmAutoInsert(false);
              }}
            >
              Yes, continue
            </Button> */}
            {/* <Button
              variant="contained"
              disabled={projectLoading}
              onClick={async () => {
                try {
                  const { data } = await getLatestProjectId({
                    variables: { domain: projectHomepage },
                  });

                  const domainUrls = data?.GetLatestProjectId?.domainUrls || [];
                  if (!domainUrls.length) {
                    console.error("⚠️ No domain URLs found");
                    return;
                  }

                  // 🔥 Call parent’s function
                  await onAutoInsertInternalLinks({ domainUrls });

                  setOpenConfirmAutoInsert(false);
                } catch (err) {
                  console.error("Failed to auto insert internal links", err);
                }
              }}
            >
              {projectLoading ? "Processing..." : "Yes, continue"}
            </Button> */}
            <Button
              variant="contained"
              disabled={autoInsertLoading}
              onClick={async () => {
                try {
                  setAutoInsertLoading(true); // 🔥 START loader

                  const { data } = await getLatestProjectId({
                    variables: { domain: projectHomepage },
                  });

                  const domainUrls = data?.GetLatestProjectId?.domainUrls || [];
                  if (!domainUrls.length) {
                    console.error("⚠️ No domain URLs found");
                    return;
                  }

                  // 🔥 WAIT until parent mutation finishes
                  await onAutoInsertInternalLinks({ domainUrls });

                  setOpenConfirmAutoInsert(false);
                } catch (err) {
                  console.error("Failed to auto insert internal links", err);
                } finally {
                  setAutoInsertLoading(false); // 🔥 STOP loader
                }
              }}
            >
              {autoInsertLoading ? "Processing..." : "Yes, continue"}
            </Button>
          </Box>
        </Box>
      </Dialog>
    </ArticleDetailStyle>
  );
}
// THIS IS THE FIRST STEP
export default ArticleDetail;
